import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  styled as MUIStyled,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import styled from '@emotion/styled';
import { useSnackbar } from 'notistack';
import { useModal } from 'mui-modal-provider';

import { venueBlockUserApi } from '../../api/users/venueBlockUserApi';
import { AccessRequestAlert } from '../../components/Alerts/AccessRequestAlert';
import UserAvatarCategory from '../../components/Avatar/UserAvatarCategory/UserAvatarCategory';
import Button from '../../components/Button/Button';
import LoadingButton from '../../components/Button/LoadingButton';
import SaveUserButton from '../../components/Button/SaveUserButton';
import {
  AccessRequestAcceptedDialog,
  accessRequestPopupStorageKey,
} from '../../components/Dialogs/AccessRequestAccepted/AccessRequestAcceptedDialog';
import { IGStatsDialog } from '../../components/Dialogs/IGStats/IGStatsDialog';
import { IGTitle } from '../../components/Dialogs/IGStats/components/IGTitle';
import FloatTopLeft from '../../components/FloatTopLeft/FloatTopLeft';
import { CollabInviteWithoutReducer } from '../../components/Flows/CollabInvite/CollabInviteWithoutReducer';
import GoBack from '../../components/GoBack/GoBack';
import TrustedIcon from '../../components/Icons/TrustedIcon/TrustedIcon';
import InstagramTag, { InstaLink } from '../../components/InstagramTag/InstagramTag';
import TikTokTag from '../../components/TikTokTag/TikTokTag';
import { Gallery } from '../../components/Photoswipe/Photoswipe';
import { PremiumTooltip } from '../../components/PremiumTooltip/PremiumTooltip';
import SkeletonWithChild from '../../components/SkeletonWithChild/SkeletonWithChild';
import TabPanelContainer, { Panel } from '../../components/TabPanelContainer/TabPanelContainer';
import { useAction } from '../../components/Tables/AccessRequest/useAction';
import {
  User,
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
  VenueAccessREnum,
} from '../../graphql/API';
import useAccessRequestQuery from '../../hooks/reactQuery/useAccessRequestQuery';
import { useQueryUser } from '../../hooks/reactQuery/useQueryUser';
import useQueryParams from '../../hooks/useQueryParams';
import useSubscriptionAccess from '../../hooks/useSubscriptionAccess';
import useVenueId from '../../hooks/useVenueId';
import { useAppSelector } from '../../redux/hooks';
import icons from '../../themes/icons';
import Icons from '../../themes/icons';
import { formatNumber } from '../../utils/formatter';
import { brandPromotionLimit } from '../../utils/reservations';
import { getUserCategory, UserCategory } from '../../utils/user';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import PanelUserContent from './components/PanelUserContent';
import PanelUserPortfolio from './components/PanelUserPortfolio';
import UserCategoryChip from './components/UserCategoryChip';

type ProfileMetricCardProps = {
  loading: boolean;
  label: string;
  value: React.ReactNode;
  sx?: SxProps<Theme>;
};

const ProfileMetricCard: React.FC<ProfileMetricCardProps> = ({ loading, label, value, sx }) => (
  <SkeletonWithChild variant='rounded' loading={loading}>
    <Box minWidth='9rem'>
      <Box sx={{ ...sx }}>
        <Typography variant='h5' fontWeight='bold' minHeight={'42px'} textAlign={'left'}>
          {value}
        </Typography>
        <Typography sx={{ color: '#848484', textAlign: 'left' }}>{label}</Typography>
      </Box>
    </Box>
  </SkeletonWithChild>
);

const ProfileStack = MUIStyled(Stack)(({ theme }) => ({
  'div:not(:first-of-type)': {
    borderLeft: '1px solid #F1F1F1',
    paddingLeft: theme.spacing(2),
  },
}));

const UserProfile: React.FC = () => {
  const { getQueryParam } = useQueryParams();
  const offerID = getQueryParam('offerID');

  const actionProps = useAction();

  const subscriptionAccess = useSubscriptionAccess();
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const { tags: venueTags } = useAppSelector((state) => state.venue.value) || {};
  const venueState = useAppSelector((state) => state.venue.value);

  const isOnlineBrand = venueTags?.includes(VENUE_MANUAL_TAGS.Online);

  const [inviteDialog, setInviteDialog] = React.useState(false);
  const [dontShowAccessRequestPopup, setDontShowAccessRequestPopup] = React.useState(false);
  const [selectedAccessRequest, setSelectedAccessRequest] =
    React.useState<VennueAccessRequestItem | null>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = React.useState<null | HTMLElement>(null);

  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();

  const { userId } = useParams<{ userId: string }>();

  const venueId = useVenueId();
  const { data, isLoading } = useQueryUser(venueId, userId);
  const { data: accessRequests } = useAccessRequestQuery({
    status: UserAccessRequestResultValue.requested,
    userID: userId ? parseInt(userId, 10) : 0,
    offerID: offerID ? parseInt(offerID, 10) : 0,
  });
  const userData = React.useMemo(() => data ?? ({} as User), [data]);

  console.log({ userData_tags: userData.tags });

  const panels = React.useMemo<Panel[]>(
    () => [
      { label: 'Portfolio', children: <PanelUserPortfolio userId={data?.id} /> },
      { label: 'Content', children: <PanelUserContent userId={data?.id} /> },
    ],
    [data?.id],
  );

  const handleAccessRequestSuccessPopup = () => {
    const showAccessRequestPopupValue = localStorage.getItem(accessRequestPopupStorageKey);
    const shouldShow = showAccessRequestPopupValue
      ? JSON.parse(showAccessRequestPopupValue)
      : false;
    setDontShowAccessRequestPopup(shouldShow);
  };

  React.useEffect(() => {
    handleAccessRequestSuccessPopup();
  }, []);

  const handleCreateInviteDialog = React.useCallback(async () => {
    if (!data) {
      return;
    }

    setInviteDialog((prev) => !prev);
  }, [data]);

  const handleIGStatsDialog = React.useCallback(() => {
    showModal(IGStatsDialog, {
      titleComponent: <IGTitle />,
      userID: userData.id,
    });
  }, [showModal, userData.id]);

  const handleMoreMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  }, []);

  const handleMoreMenuClose = React.useCallback(() => {
    setMoreMenuAnchor(null);
  }, []);

  const handleBlockMember = React.useCallback(async () => {
    if (!venueId || !userData?.id) return;
    try {
      await venueBlockUserApi({
        venueID: Number(venueId),
        userID: Number(userData.id),
      });
      enqueueSnackbar('You have successfully blocked the member', { variant: 'success' });
      handleMoreMenuClose();
      window.history.back();
    } catch {
      enqueueSnackbar('Failed to block member', { variant: 'error' });
    }
  }, [venueId, userData?.id, enqueueSnackbar, handleMoreMenuClose]);

  const fullProfile = React.useMemo(
    () => subscriptionAccess?.includes('COMPLETE_USER_PROFILES'),
    [subscriptionAccess],
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isPromotionLimitHit = React.useMemo(
    () =>
      (venueState?.totalConsumedPromotions ?? 0) >= (venueState?.totalAllowedPromotions ?? 0),
    [venueState?.totalConsumedPromotions, venueState?.totalAllowedPromotions],
  );

  const hasActiveSubscription = !!subscription && subscription.status === 'active';

  return (
    <>
      <Helmet>
        <title>{`Beautypass - ${userData.firstname} ${userData.lastname}`}</title>
      </Helmet>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backgroundColor: 'white',
          flex: 1,
          py: inviteDialog ? 0 : 4,
          pl: inviteDialog ? 0 : 9,
          pr: inviteDialog ? 0 : 6,
        }}
      >
        {inviteDialog && data ? (
          <CollabInviteWithoutReducer users={[data]} onBack={handleCreateInviteDialog} />
        ) : (
          <>
            <FloatTopLeft>
              <GoBack />
            </FloatTopLeft>
            <Box
              sx={{
                display: 'flex',
                backgroundColor: '#FAFAFA',
                borderRadius: 1,
                px: 4,
                pt: 2,
                pb: 4,
                mb: 4,
              }}
            >
              {isLoading && <Skeleton variant='circular' width={'15rem'} height={'15rem'} />}
              {!isLoading && userData && (
                <UserAvatarCategory
                  showUserCategory={false}
                  variant='circular'
                  alt={userData.firstname}
                  placeholderSrc={String(userData.picture?.thumbnail)}
                  src={String(userData.picture?.medium)}
                  userCategory={userData.lifestyle ? UserCategory.lifestyle : UserCategory.select}
                  sx={{
                    width: '15rem',
                    height: '15rem',
                  }}
                />
              )}
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'space-between'}
                sx={{ width: '100%', ml: 3, py: 1 }}
              >
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography component='div' sx={{ display: 'flex', alignItems: 'center' }}>
                      <SkeletonWithChild width='100%' loading={isLoading}>
                        <Typography fontWeight={600} fontSize={'26px'}>
                          {userData.firstname} {userData.lastname}
                        </Typography>
                        {/* {userData.agencyProfileUrl && (
                          <TrustedIcon sx={{ fontSize: '1.2rem', ml: 1 }} />
                        )} */}
                      </SkeletonWithChild>
                    </Typography>

                    <SkeletonWithChild width='80%' loading={isLoading}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '8.5px',
                        }}
                      >
                        <Stack direction='row' spacing={1.5} flexWrap='wrap' useFlexGap>
                          {(fullProfile || !hasActiveSubscription) && userData.instagramHandle && !isPromotionLimitHit && (
                            <SocialCard>
                              <Icons.InstagramActiveV2Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
                              {hasActiveSubscription &&
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                  <InstagramTag
                                    username={userData.instagramHandle}
                                    showAtSign={false}
                                    textColor='#000'
                                    sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                                  />
                                  {userData.instagramVerified && hasActiveSubscription && (
                                    <Box
                                      component='img'
                                      src='/png/instagramVerified_icon@3x.png'
                                      alt='Verified'
                                      sx={{ width: 14, height: 14, flexShrink: 0 }}
                                    />
                                  )}
                                  {(userData.instagramFollowers ?? 0) > 0 && hasActiveSubscription && (
                                    <Typography component='span' sx={{ fontSize: '0.875rem', color: '#000' }}>
                                      · {formatNumber(userData.instagramFollowers ?? 0)}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            </SocialCard>
                          )}
                          {(fullProfile || !hasActiveSubscription) && userData.tiktokHandle && !isPromotionLimitHit && (
                            <SocialCard>
                              <Icons.TiktokIcon width={10.61} height={13.43} style={{ objectFit: 'contain' }} />
                              {hasActiveSubscription &&
                                <TikTokTag
                                  tiktokHandle={userData.tiktokHandle}
                                  showAtSign={false}
                                  textColor='#000'
                                  sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                                />
                              }
                            </SocialCard>
                          )}
                        </Stack>

                        {(userData.tags?.filter(Boolean).length ?? 0) > 0 && (
                          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap alignItems='center'>
                            {userData.agencyProfileUrl && (
                              <VerifiedModelChip key='verified-model'>
                                <TrustedIcon sx={{ width: '12.8px', height: '15px', mr: '10px' }} />
                                <span>Verified Model</span>
                              </VerifiedModelChip>
                            )}
                            {userData.tags?.filter(Boolean).map((tag) => {
                              if (!tag) return null;
                              const userCategory = getUserCategory(userData.tags ?? []);
                              const isCategoryTag = userCategory && tag === userCategory;
                              if (isCategoryTag && userCategory) {
                                return (
                                  <UserCategoryChip
                                    key={tag}
                                    userCategory={userCategory as UserCategory}
                                  />
                                );
                              }
                              return null;
                            })}
                          </Stack>
                        )}

                        {/* {data?.agencyProfileUrl && data?.agency && (
                          <InstagramBox>
                            <InstaLink
                              component='a'
                              target='_blank'
                              href={data?.agencyProfileUrl || ''}
                              textColor={'#1E55A1'}
                              fontWeight={500}
                            >
                              {data?.agency}
                            </InstaLink>
                          </InstagramBox>
                        )} */}
                      </Box>
                    </SkeletonWithChild>
                  </Box>
                  <Stack spacing={4} direction='row' alignItems='flex-start'>
                    <SaveUserButton
                      size='large'
                      userId={Number(userData.id)}
                      saved={Boolean(userData.savedByVenue)}
                      isOnlineBrand
                    ></SaveUserButton>

                    <IconButton
                      aria-label='More options'
                      onClick={handleMoreMenuOpen}
                      sx={{
                        backgroundColor: '#ececec',
                        color: '#000',
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#e0e0e0' },
                      }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu
                      anchorEl={moreMenuAnchor}
                      open={Boolean(moreMenuAnchor)}
                      onClose={handleMoreMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      slotProps={{
                        paper: {
                          sx: {
                            borderRadius: '10px',
                            minWidth: 180,
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
                            border: '1px solid #E0E0E0',
                            py: 0.5,
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={handleBlockMember}
                        sx={{
                          color: '#E93F3F',
                          gap: 1.5,
                          py: 1.5,
                          px: 2,
                          borderRadius: '8px',
                          mx: 0.5,
                          fontWeight: 500,
                          opacity: 1,
                          '& .MuiSvgIcon-root': { color: '#E93F3F', fontSize: 20, opacity: 1 },
                          '& svg': { width: 20, height: 20, opacity: 1 },
                          '& svg path': { fill: '#E93F3F', opacity: 1 },
                          '& svg g': { opacity: 1 },
                        }}
                      >
                        <Icons.BlockedUserIcon style={{ fontSize: 20, opacity: 1 }} />
                        Block member
                      </MenuItem>
                    </Menu>

                    {subscriptionPlan === 'starter' || subscriptionPlan === 'plus' ? (
                      <Button
                        color='secondary'
                        variant='contained'
                        size='large'
                        onClick={handleIGStatsDialog}
                        sx={{ px: 4, color: '#000000', backgroundColor: '#ececec' }}
                        startIcon={<icons.StatsIcon />}
                      >
                        Analytics
                      </Button>
                    ) : (
                      <PremiumTooltip showStarterIcon>
                        <Button
                          color='secondary'
                          variant='contained'
                          size='large'
                          onClick={handleIGStatsDialog}
                          sx={{
                            px: 4,
                            color: '#000000',
                            backgroundColor: '#ececec',
                            '&:disabled': {
                              color: '#000000',
                            },
                          }}
                          startIcon={<icons.StatsIcon />}
                          disabled
                        >
                          Analytics
                        </Button>
                      </PremiumTooltip>
                    )}

                    {!offerID ? (
                      <>
                        {!data?.isEventReserved && (
                          <Button
                            variant='contained'
                            size='large'
                            sx={{ px: 4 }}
                            onClick={handleCreateInviteDialog}
                          >
                            {userData ? `Invite ${userData?.firstname}` : 'Invite'}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Stack spacing={1} direction='row'>
                        <LoadingButton
                          fullWidth
                          variant='contained'
                          onClick={() => {
                            if (offerID) {
                              if (accessRequests.length)
                                setSelectedAccessRequest(accessRequests[0]);
                              actionProps.onAction({
                                action: VenueAccessREnum.accepted,
                                offerID: parseInt(offerID, 10),
                                userID: parseInt(userData.id, 10),
                              });
                            }
                          }}
                          loading={
                            actionProps.loadingState[
                            `${userData?.id}:${offerID}:${VenueAccessREnum.accepted}`
                            ]
                          }
                          disabled={
                            actionProps.loadingState[
                            `${userData?.id}:${offerID}:${VenueAccessREnum.accepted}`
                            ] || actionProps.successState
                          }
                          sx={{ py: 1 }}
                        >
                          Accept
                        </LoadingButton>
                        <LoadingButton
                          fullWidth
                          variant='outlined'
                          onClick={() => {
                            if (offerID) {
                              if (accessRequests.length) setSelectedAccessRequest(null);
                              actionProps.onAction({
                                action: VenueAccessREnum.declined,
                                offerID: parseInt(offerID, 10),
                                userID: parseInt(userData.id, 10),
                              });
                            }
                          }}
                          loading={
                            actionProps.loadingState[
                            `${userData?.id}:${offerID}:${VenueAccessREnum.declined}`
                            ]
                          }
                          disabled={
                            actionProps.loadingState[
                            `${userData?.id}:${offerID}:${VenueAccessREnum.declined}`
                            ] || actionProps.successState
                          }
                          sx={{ py: 1 }}
                        >
                          Reject
                        </LoadingButton>
                      </Stack>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <ProfileStack spacing={4} direction='row'>
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Average rating'}
                      value={
                        <Typography
                          component='div'
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            justifyContent: 'flex-start',
                          }}
                        >
                          {(userData?.rating || 0) > 0 ? (
                            <>
                              <StarIcon sx={{ fontSize: '1.1rem', mr: 0.5 }} />
                              <Box component='span' fontWeight='bold' fontSize={'28px'}>
                                {userData?.rating?.toFixed(1)}
                              </Box>
                              <Typography component={'span'} color='#000' mx={0.5}>
                                ({userData.totalRatings})
                              </Typography>
                            </>
                          ) : (
                            <Typography component={'span'} color='#000' mx={0.5}>
                              no ratings yet
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Followers'}
                      value={formatNumber(userData.instagramFollowers ?? 0)}
                      sx={{ pl: 2 }}
                    />
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Total promotions'}
                      value={data?.totalRequirementsSubmitted}
                      sx={{ pl: 2 }}
                    />
                    <ProfileMetricCard
                      loading={isLoading}
                      label={'Market'}
                      value={userData?.marketName}
                      sx={{ pl: 2 }}
                    />
                  </ProfileStack>
                </Box>
              </Box>
            </Box>

            <Gallery>
              <TabPanelContainer panels={panels} defaultValue={(data?.totalPortfolioContents || 0) > 0 ? 0 : (data?.totalContentUploaded || 0) > 0 ? 1 : 0} />
            </Gallery>
          </>
        )}
      </Box>
      <AccessRequestAlert
        open={
          ((selectedAccessRequest || false) &&
            actionProps.successState &&
            isOnlineBrand &&
            !!offerID &&
            dontShowAccessRequestPopup) ||
          false
        }
        onClose={() => {
          setSelectedAccessRequest(null);
          actionProps.setSuccessState(false);
        }}
        accessRequest={selectedAccessRequest}
      />
      {!dontShowAccessRequestPopup && (
        <AccessRequestAcceptedDialog
          open={
            ((selectedAccessRequest || false) &&
              actionProps.successState &&
              isOnlineBrand &&
              !dontShowAccessRequestPopup &&
              !!offerID) ||
            false
          }
          accessRequest={selectedAccessRequest}
          onClose={() => {
            actionProps.setSuccessState(false);
            setSelectedAccessRequest(null);
          }}
          reservationID={actionProps.reservationID}
        />
      )}
    </>
  );
};

export default UserProfile;

const InstagramBox = styled.div`
  display: inline-flex;
  padding: 2px 8px;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  background-color: #ececec;
`;

const SocialCard = styled.div`
  display: inline-flex;
  padding: 4px 10px;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  background-color: #f5f5f5;
  border: 1px solid #efefef;
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #1E55A1;
`;

const VerifiedModelChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  line-height: 100%;
  color: #f5789f;
  background-color: #f7f7f7;
  border: 1px solid #f0f0f0;
`;

const TagChip = styled(Chip)`
  font-weight: 500;
  font-size: 15px;
  line-height: 100%;
  border-radius: 6px;
  &.MuiChip-root {
    background-color: #f7f7f7;
    color: #111111;
    border: 1px solid #f0f0f0;
    padding: 4px 10px;
    height: auto;
  }
  & .MuiChip-label {
    padding: 0;
  }
`;
